import React, { useState } from 'react';
import { Mail, Check, X, RefreshCw, Filter, Globe, ExternalLink } from 'lucide-react';
import { EmailWithSelection } from '../types';

interface EmailSelectionProps {
  emails: EmailWithSelection[];
  onToggleSelection: (email: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

export default function EmailSelection({ 
  emails, 
  onToggleSelection, 
  searchTerm, 
  setSearchTerm 
}: EmailSelectionProps) {
  const [sortBy, setSortBy] = useState<'domain' | 'email' | 'country'>('email');
  const [filterDomain, setFilterDomain] = useState<string | null>(null);
  const [filterCountry, setFilterCountry] = useState<string | null>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Get all unique domains
  const domains = [...new Set(emails.map(item => item.email.split('@')[1]))].sort();
  
  // Get all unique countries
  const countries = [...new Set(emails.map(item => item.country).filter(Boolean))] as string[];

  // Get country name from code
  const getCountryName = (code: string) => {
    const countryNames: Record<string, string> = {
      'US': 'United States',
      'GB': 'United Kingdom',
      'CA': 'Canada',
      'AU': 'Australia',
      'DE': 'Germany',
      'FR': 'France',
      'IN': 'India',
      'JP': 'Japan',
      'BR': 'Brazil',
      'IT': 'Italy',
      'ES': 'Spain',
      'NL': 'Netherlands',
      'MT': 'Malta'
    };
    return countryNames[code] || code;
  };

  // Filter emails based on search term, domain filter, and country filter
  const filteredEmails = emails.filter(item => {
    const matchesSearch = searchTerm 
      ? item.email.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
      
    const matchesDomain = filterDomain
      ? item.email.endsWith('@' + filterDomain)
      : true;
    
    const matchesCountry = filterCountry
      ? item.country === filterCountry
      : true;
      
    return matchesSearch && matchesDomain && matchesCountry;
  });

  // Sort emails
  const sortedEmails = [...filteredEmails].sort((a, b) => {
    if (sortBy === 'domain') {
      const domainA = a.email.split('@')[1];
      const domainB = b.email.split('@')[1];
      return domainA.localeCompare(domainB);
    } else if (sortBy === 'country') {
      const countryA = a.country || '';
      const countryB = b.country || '';
      return countryA.localeCompare(countryB);
    } else {
      return a.email.localeCompare(b.email);
    }
  });

  const toggleSortBy = (field: 'domain' | 'email' | 'country') => {
    setSortBy(field);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterDomain(null);
    setFilterCountry(null);
  };

  const selectAll = () => {
    filteredEmails.forEach(item => {
      if (!item.selected) {
        onToggleSelection(item.email);
      }
    });
  };

  const deselectAll = () => {
    filteredEmails.forEach(item => {
      if (item.selected) {
        onToggleSelection(item.email);
      }
    });
  };

  return (
    <div className="mt-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Mail className="h-5 w-5 mr-2 text-blue-500" />
            Email Recipients ({emails.length})
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {emails.filter(e => !e.selected).length} will be sent
            </span>
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Search emails..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 pr-10 py-1 border rounded-md text-sm w-60"
              />
              <Mail className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              
              <button 
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2"
              >
                <Filter className={`h-4 w-4 ${(filterDomain || filterCountry) ? 'text-blue-500' : 'text-gray-400'}`} />
              </button>
              
              {showFilterDropdown && (
                <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-10 w-64 max-h-80 overflow-y-auto">
                  <div className="p-2 border-b">
                    <div className="text-sm font-medium mb-1">Filter Options</div>
                    <div className="flex justify-between items-center">
                      <button 
                        onClick={clearFilters}
                        className="text-xs text-blue-500 hover:text-blue-700 flex items-center"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Clear Filters
                      </button>
                      <button 
                        onClick={() => setShowFilterDropdown(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  
                  {countries.length > 0 && (
                    <div className="p-2 border-b">
                      <div className="text-sm font-medium mb-1 flex items-center">
                        <Globe className="h-3 w-3 mr-1 text-blue-500" />
                        Filter by Country
                      </div>
                      <div className="max-h-40 overflow-y-auto">
                        {countries.sort().map(country => (
                          <button
                            key={country}
                            onClick={() => {
                              setFilterCountry(country === filterCountry ? null : country);
                              setShowFilterDropdown(false);
                            }}
                            className={`block w-full text-left px-2 py-1 text-sm rounded ${
                              country === filterCountry ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                            }`}
                          >
                            {getCountryName(country)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="p-2">
                    <div className="text-sm font-medium mb-1">Filter by Domain</div>
                    <div className="max-h-40 overflow-y-auto">
                      {domains.map(domain => (
                        <button
                          key={domain}
                          onClick={() => {
                            setFilterDomain(domain === filterDomain ? null : domain);
                            setShowFilterDropdown(false);
                          }}
                          className={`block w-full text-left px-2 py-1 text-sm rounded ${
                            domain === filterDomain ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                          }`}
                        >
                          @{domain}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 border-b">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-5 flex items-center">
                <button 
                  onClick={() => toggleSortBy('email')}
                  className={`flex items-center ${sortBy === 'email' ? 'text-blue-600' : ''}`}
                >
                  Email
                  {sortBy === 'email' && <span className="ml-1">↓</span>}
                </button>
              </div>
              <div className="col-span-3 flex items-center">
                <button 
                  onClick={() => toggleSortBy('domain')}
                  className={`flex items-center ${sortBy === 'domain' ? 'text-blue-600' : ''}`}
                >
                  Domain
                  {sortBy === 'domain' && <span className="ml-1">↓</span>}
                </button>
              </div>
              <div className="col-span-2 flex items-center">
                <button 
                  onClick={() => toggleSortBy('country')}
                  className={`flex items-center ${sortBy === 'country' ? 'text-blue-600' : ''}`}
                >
                  Country
                  {sortBy === 'country' && <span className="ml-1">↓</span>}
                </button>
              </div>
              <div className="col-span-2 text-center">
                <div className="flex justify-end space-x-1">
                  <button 
                    onClick={selectAll} 
                    className="text-xs text-blue-500 hover:text-blue-700 p-1"
                    title="Select all"
                  >
                    All
                  </button>
                  <span className="text-gray-400">|</span>
                  <button 
                    onClick={deselectAll} 
                    className="text-xs text-blue-500 hover:text-blue-700 p-1"
                    title="Deselect all"
                  >
                    None
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {sortedEmails.length > 0 ? (
              sortedEmails.map((item) => {
                const domain = item.email.split('@')[1];
                return (
                  <div
                    key={item.email}
                    className={`w-full px-4 py-2 text-sm hover:bg-gray-50 border-b last:border-0 grid grid-cols-12 gap-4 ${
                      item.selected ? 'bg-red-50' : ''
                    }`}
                  >
                    <div className="col-span-5 text-left truncate flex items-center">
                      <button 
                        onClick={() => onToggleSelection(item.email)}
                        className="mr-2 text-left flex-grow truncate hover:underline"
                      >
                        {item.email}
                      </button>
                      
                      {item.sourceUrl && item.sourceName && (
                        <a 
                          href={item.sourceUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                          title={`View source: ${item.sourceName}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                    <div className="col-span-3 text-left text-gray-500 truncate">@{domain}</div>
                    <div className="col-span-2 text-left text-gray-500">
                      {item.country && (
                        <span className="inline-flex items-center">
                          <Globe className="h-3 w-3 mr-1 text-blue-400" />
                          {item.country}
                        </span>
                      )}
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <button
                        onClick={() => onToggleSelection(item.email)}
                        className={`inline-flex items-center ${item.selected ? 'text-red-500' : 'text-green-500'}`}
                      >
                        {item.selected ? (
                          <>
                            <X className="h-4 w-4 mr-1" /> Excluded
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" /> Included
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-6 text-center text-gray-500">
                No emails found matching your criteria
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between mt-3 text-sm text-gray-500">
          <span>Click on an email to exclude/include it in the campaign</span>
          <span>{emails.filter(e => e.selected).length} excluded / {emails.length} total</span>
        </div>
        
        <div className="mt-2 text-sm flex items-center space-x-2">
          {filterDomain && (
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center">
              Domain: @{filterDomain}
              <button 
                onClick={() => setFilterDomain(null)}
                className="ml-1 text-blue-700 hover:text-blue-900"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          
          {filterCountry && (
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center">
              Country: {getCountryName(filterCountry)}
              <button 
                onClick={() => setFilterCountry(null)}
                className="ml-1 text-blue-700 hover:text-blue-900"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}